import { Component, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule, ActivatedRoute } from '@angular/router';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { AuthService } from '../../../services/auth.service';

@Component({
  selector: 'app-sign-in',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    InputTextModule,
    ButtonModule,
    ProgressSpinnerModule
  ],
  templateUrl: './sign-in.component.html',
  styleUrl: './sign-in.component.css'
})
export class SignInComponent {
  email = signal('');
  password = signal('');
  otp = signal('');
  loading = signal(false);
  error = signal<string | null>(null);
  showOtpStep = signal(false);

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private authService: AuthService
  ) {}

  onSubmit(): void {
    if (this.showOtpStep()) {
      this.verifyOtp();
      return;
    }

    const emailValue = this.email().trim();
    const passwordValue = this.password().trim();
    
    if (!emailValue) {
      this.error.set('Please enter your email address');
      return;
    }

    if (!this.isValidEmail(emailValue)) {
      this.error.set('Please enter a valid email address');
      return;
    }

    if (!passwordValue) {
      this.error.set('Please enter your password');
      return;
    }

    this.loading.set(true);
    this.error.set(null);

    this.authService.sendLoginOtp({
      email: emailValue,
      password: passwordValue
    }).subscribe({
      next: (response) => {
        this.loading.set(false);
        if (response.success) {
          this.showOtpStep.set(true);
        } else {
          this.error.set(response.message || 'Failed to send verification code');
        }
      },
      error: (err) => {
        this.loading.set(false);
        this.error.set(err.error?.message || 'An error occurred. Please try again.');
      }
    });
  }

  verifyOtp(): void {
    const emailValue = this.email().trim();
    const otpValue = this.otp().trim();

    if (!otpValue) {
      this.error.set('Please enter the verification code');
      return;
    }

    if (otpValue.length !== 6) {
      this.error.set('Verification code must be 6 digits');
      return;
    }

    this.loading.set(true);
    this.error.set(null);

    this.authService.verifyLoginOtp({
      email: emailValue,
      otp: otpValue
    }).subscribe({
      next: (response) => {
        this.loading.set(false);
        if (response.success) {
          const returnUrl = this.route.snapshot.queryParams['returnUrl'] as string | undefined;
          const url = returnUrl && returnUrl.startsWith('/') && !returnUrl.startsWith('//')
            ? returnUrl
            : '/marketplace';
          this.router.navigateByUrl(url);
        } else {
          this.error.set(response.message || 'Invalid verification code');
        }
      },
      error: (err) => {
        this.loading.set(false);
        this.error.set(err.error?.message || 'Invalid verification code. Please try again.');
      }
    });
  }

  backToPassword(): void {
    this.showOtpStep.set(false);
    this.otp.set('');
    this.error.set(null);
  }

  private isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }
}
